import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  Check,
  Clipboard,
  Loader2,
  Pause,
  Play,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";

import { getDocIdData } from "../../api/universal";
import { useRightSidebar } from "../contexts/RightSidebarContext";
import {
  claimAudioSource,
  getActiveAudioSourceId,
  registerAudioSource,
  releaseAudioSource,
  subscribeToActiveAudioSource,
} from "../../utils/audioPlaybackManager";
import { formatPostDate } from "../../utils/postDate";

const cyan = "14,165,233";

const SOURCE_LABELS = {
  "ispot.tv": "iSpot.tv",
  ispot: "iSpot.tv",
};

const getDocId = (article, cardId) =>
  cardId ||
  article?.document_id ||
  article?.doc_id ||
  article?.docID ||
  article?.id ||
  null;

const unwrapResponseData = (res) => {
  const data = res?.data ?? res;

  if (Array.isArray(data)) return data[0] ?? null;
  if (Array.isArray(data?.data)) return data.data[0] ?? null;
  if (data?.data && typeof data.data === "object") return data.data;

  return data ?? null;
};

const fetchDocDetails = async (docId) => {
  if (!docId) return null;

  try {
    const res = await getDocIdData(docId);
    return unwrapResponseData(res);
  } catch (firstError) {
    try {
      const res = await getDocIdData({ doc_id: docId });
      return unwrapResponseData(res);
    } catch {
      throw firstError;
    }
  }
};

const normalizeContent = (content) => {
  if (!content) return "";

  return String(content)
    .replace(/\s+/g, " ")
    .replace(/\s+\./g, ".")
    .replace(/\s+,/g, ",")
    .trim();
};

const cleanTitle = (item) => {
  const title = String(item?.title || "").trim();

  if (!title) return "Untitled";

  return title
    .replace(/^Thumbnail for\s+/i, "")
    .replace(/\s+-\s+iSpot\s*$/i, "")
    .trim();
};

const getSourceLabel = (source) => {
  if (!source) return "";

  const normalized = String(source).trim().replace(/^#/, "");
  return SOURCE_LABELS[normalized.toLowerCase()] || normalized;
};

const getBestUrl = (item) => item?.url || item?.title || "";

const getPreviewImage = (item) =>
  item?.screenshot_full ||
  item?.screenshot ||
  item?.thumbnail_url ||
  item?.thumbnailUrl ||
  item?.image_url ||
  item?.imageUrl ||
  "";

const getVideoUrl = (item) => {
  const video = item?.video || item?.video_url || item?.videoUrl || "";
  return typeof video === "string" && video.trim() ? video.trim() : "";
};

const isValidHttpUrl = (value) => {
  if (!value || typeof value !== "string") return false;

  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
};

// MM-DD-YYYY, nothing relative
const formatDate = formatPostDate;

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";

  const total = Math.floor(seconds);
  const mins = Math.floor(total / 60);
  const secs = total % 60;

  return `${mins}:${String(secs).padStart(2, "0")}`;
};

const mergeArticleData = (baseArticle, fetchedArticle) => ({
  ...(baseArticle ?? {}),
  ...(fetchedArticle ?? {}),
});

const LoadingSpinner = ({ className = "h-4 w-4" }) => (
  <Loader2 className={`${className} animate-spin`} strokeWidth={2} />
);

const CopyableUrl = ({ url }) => {
  const [copied, setCopied] = useState(false);
  const [overflowPx, setOverflowPx] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  const viewportRef = useRef(null);
  const textRef = useRef(null);

  if (!url) return null;

  const measureOverflow = () => {
    const viewport = viewportRef.current;
    const text = textRef.current;

    if (!viewport || !text) return;

    const overflow = Math.max(0, text.scrollWidth - viewport.clientWidth);
    setOverflowPx(overflow);
  };

  const handleCopy = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error("[ArticleCardDetailPanel] Failed to copy URL:", err);
    }
  };

  const shouldScroll = isHovering && overflowPx > 0;
  const scrollDuration = Math.min(10, Math.max(2.5, overflowPx / 42));

  return (
    <button
      type="button"
      onClick={handleCopy}
      onMouseEnter={() => {
        measureOverflow();
        setIsHovering(true);
      }}
      onMouseLeave={() => setIsHovering(false)}
      className={[
        "flex h-7 w-full min-w-0 items-center gap-1.5 rounded-lg border bg-innerBg",
        "px-2 text-left transition hover:bg-btnHover",
      ].join(" ")}
      style={{
        borderColor: `rgba(${cyan},0.12)`,
      }}
      title="Copy URL"
    >
      <span
        ref={viewportRef}
        className="flex h-full min-w-0 flex-1 items-center overflow-hidden"
      >
        <span
          ref={textRef}
          className="inline-block whitespace-nowrap text-[10px] leading-none text-mutedText transition-transform ease-linear"
          style={{
            transform: shouldScroll
              ? `translateX(-${overflowPx}px)`
              : "translateX(0)",
            transitionDuration: shouldScroll ? `${scrollDuration}s` : "180ms",
          }}
        >
          {url}
        </span>
      </span>

      <span className="flex h-full shrink-0 items-center justify-center">
        {copied ? (
          <Check className="h-3 w-3 text-velBlue" strokeWidth={2.2} />
        ) : (
          <Clipboard className="h-3 w-3 text-mutedText" strokeWidth={2.2} />
        )}
      </span>
    </button>
  );
};

const BrandedVideoPlayer = ({ article, src, onError }) => {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const idleTimerRef = useRef(null);

  const audioSourceId = useMemo(() => {
    const id = getDocId(article);
    return `detail:${id || src}`;
  }, [article, src]);

  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isAudioClaimed, setIsAudioClaimed] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [showControls, setShowControls] = useState(true);

  const armIdleHide = useCallback(() => {
    clearTimeout(idleTimerRef.current);
    setShowControls(true);

    const video = videoRef.current;
    if (!video || video.paused) return;

    idleTimerRef.current = window.setTimeout(() => {
      setShowControls(false);
    }, 2200);
  }, []);

  useEffect(() => {
    return () => {
      clearTimeout(idleTimerRef.current);
      releaseAudioSource(audioSourceId);
    };
  }, [audioSourceId]);

  useEffect(() => {
    setIsReady(false);
    setIsPlaying(false);
    setIsMuted(true);
    setIsAudioClaimed(false);
    setCurrentTime(0);
    setDuration(0);
    setShowControls(true);

    releaseAudioSource(audioSourceId);
  }, [src, audioSourceId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    return registerAudioSource(audioSourceId, video);
  }, [audioSourceId, src]);

  useEffect(() => {
    return subscribeToActiveAudioSource((nextActiveAudioSourceId) => {
      if (nextActiveAudioSourceId !== audioSourceId) {
        setIsMuted(true);
        setIsAudioClaimed(false);
      }
    });
  }, [audioSourceId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = isMuted;
  }, [isMuted]);

  const autoplayVideo = () => {
    const video = videoRef.current;
    if (!video) return;

    video.muted = true;
    setIsMuted(true);

    const playPromise = video.play();
    if (playPromise?.catch) {
      playPromise.catch(() => {
        setIsPlaying(false);
        setShowControls(true);
      });
    }
  };

  const handleReady = () => {
    setIsReady(true);
    autoplayVideo();
  };

  const togglePlay = (event) => {
    event?.stopPropagation();

    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
    } else {
      video.pause();
    }
  };

  const toggleMute = (event) => {
    event?.stopPropagation();

    const video = videoRef.current;
    if (!video) return;

    if (isMuted) {
      claimAudioSource(audioSourceId);
      setIsAudioClaimed(true);
      setIsMuted(false);
      video.muted = false;

      const playPromise = video.play();
      if (playPromise?.catch) playPromise.catch(() => {});
      return;
    }

    setIsMuted(true);
    setIsAudioClaimed(false);
    video.muted = true;

    if (getActiveAudioSourceId() === audioSourceId) {
      releaseAudioSource(audioSourceId);
    }
  };

  const handleSeek = (event) => {
    event.stopPropagation();

    const video = videoRef.current;
    const bar = progressRef.current;

    if (!video || !bar || !duration) return;

    const rect = bar.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;

    video.currentTime = Math.max(0, Math.min(duration, ratio * duration));
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const showOverlayPlay = isReady && !isPlaying;

  return (
    <div
      onMouseMove={armIdleHide}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      className="relative w-full overflow-hidden rounded-xl border border-surfaceBorder bg-black"
      style={{ aspectRatio: "16 / 9" }}
    >
      {!isReady && (
        <div className="absolute inset-0 flex items-center justify-center bg-black">
          <LoadingSpinner className="h-5 w-5 text-white/45" />
        </div>
      )}

      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        playsInline
        preload="auto"
        onLoadedData={handleReady}
        onCanPlay={handleReady}
        onPlay={() => {
          setIsPlaying(true);
          armIdleHide();
        }}
        onPause={() => {
          setIsPlaying(false);
          setShowControls(true);
        }}
        onTimeUpdate={(event) =>
          setCurrentTime(event.currentTarget.currentTime || 0)
        }
        onLoadedMetadata={(event) =>
          setDuration(event.currentTarget.duration || 0)
        }
        onVolumeChange={(event) => {
          const video = event.currentTarget;
          setIsMuted(video.muted || video.volume === 0);
        }}
        onError={onError}
        onClick={togglePlay}
        className={[
          "h-full w-full cursor-pointer object-contain transition-opacity duration-200",
          isReady ? "opacity-100" : "opacity-0",
        ].join(" ")}
      />

      {showOverlayPlay && (
        <button
          type="button"
          onClick={togglePlay}
          className="group absolute inset-0 flex items-center justify-center bg-black/30 transition hover:bg-black/40"
          aria-label="Play video"
        >
          <span className="flex h-14 w-14 items-center justify-center rounded-full border border-white/25 bg-black/55 text-white shadow-login-panel backdrop-blur-md transition group-hover:scale-105 group-hover:bg-black/70 group-hover:border-white/40 group-active:scale-95">
            <Play size={22} fill="currentColor" className="ml-0.5" />
          </span>
        </button>
      )}

      <div
        className={[
          "pointer-events-none absolute inset-x-0 bottom-0 px-2.5 pb-2 pt-8 transition-opacity duration-200",
          showControls && isReady ? "opacity-100" : "opacity-0",
        ].join(" ")}
        style={{
          background:
            "linear-gradient(to top, rgba(0,0,0,0.86) 0%, rgba(0,0,0,0.42) 52%, transparent 100%)",
        }}
      >
        <div
          ref={progressRef}
          onClick={handleSeek}
          className="group/progress pointer-events-auto mb-2 flex h-3 w-full cursor-pointer items-center"
          role="slider"
          aria-label="Seek"
          aria-valuemin={0}
          aria-valuemax={duration || 0}
          aria-valuenow={currentTime}
        >
          <div className="relative h-1 w-full overflow-hidden rounded-full bg-white/15 transition-colors group-hover/progress:bg-white/25">
            <div
              className="h-full bg-velBlue/75 transition-[width] duration-150"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        <div className="pointer-events-auto flex items-center gap-2">
          <button
            type="button"
            onClick={togglePlay}
            className="rounded-full border border-white/15 bg-black/45 p-1.5 text-white/85 backdrop-blur-md transition hover:bg-black/60 hover:text-white active:scale-95"
            aria-label={isPlaying ? "Pause video" : "Play video"}
          >
            {isPlaying ? <Pause size={12} /> : <Play size={12} />}
          </button>

          <span className="text-[10px] font-medium tabular-nums">
            <span className="text-white/90">{formatTime(currentTime)}</span>
            <span className="text-white/40"> / {formatTime(duration)}</span>
          </span>

          <div className="flex-1" />

          <button
            type="button"
            onClick={toggleMute}
            className={[
              "rounded-full border p-1.5 backdrop-blur-md transition active:scale-95",
              isMuted
                ? "border-white/15 bg-black/45 text-white/85 hover:bg-black/60 hover:text-white"
                : "border-velBlue/35 bg-velBlue/20 text-velBlue",
            ].join(" ")}
            aria-label={isMuted ? "Unmute video" : "Mute video"}
            title={isAudioClaimed ? "Audio active" : "Unmute"}
          >
            {isMuted ? <VolumeX size={12} /> : <Volume2 size={12} />}
          </button>
        </div>
      </div>
    </div>
  );
};

const ImagePreview = ({ article }) => {
  const image = getPreviewImage(article);

  if (!image) {
    return (
      <div className="flex aspect-video w-full items-center justify-center rounded-xl border border-surfaceBorder bg-innerBg p-8 text-center text-sm text-mutedText">
        No preview available.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-surfaceBorder bg-black">
      <img src={image} alt="" className="h-full w-full object-contain" />
    </div>
  );
};

const ExpandableDetailContent = ({ content }) => {
  const [expanded, setExpanded] = useState(false);

  if (!content) return null;

  const shouldClamp = content.length > 280;

  return (
    <section className="rounded-2xl border border-surfaceBorder bg-surface/70 p-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-mutedText">
        Content
      </h3>

      <p
        className={[
          "whitespace-pre-wrap text-sm leading-6 text-mainText/90",
          expanded ? "" : "line-clamp-5",
        ].join(" ")}
      >
        {content}
      </p>

      {shouldClamp && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-2 text-xs font-medium text-velBlue/85 transition hover:text-velBlue"
        >
          {expanded ? "Show less" : "Read more"}
        </button>
      )}
    </section>
  );
};

const ArticleCardDetailPanel = ({
  article,
  cardId,
  onClose,
  onInteract = () => {},
}) => {
  const docId = getDocId(article, cardId);

  const [fetchedArticle, setFetchedArticle] = useState(null);
  const [status, setStatus] = useState(docId ? "loading" : "ready");
  const [error, setError] = useState("");
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadDoc = async () => {
      if (!docId) {
        setStatus("ready");
        return;
      }

      setStatus("loading");
      setError("");
      setFetchedArticle(null);
      setVideoFailed(false);

      try {
        const docData = await fetchDocDetails(docId);

        if (cancelled) return;

        setFetchedArticle(docData);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;

        console.error("[ArticleCardDetailPanel] Failed to load doc:", err);
        setError(err?.message || "Failed to load document details.");
        setStatus("error");
      }
    };

    loadDoc();

    return () => {
      cancelled = true;
    };
  }, [docId]);

  const mergedArticle = useMemo(
    () => mergeArticleData(article, fetchedArticle),
    [article, fetchedArticle],
  );

  const title = cleanTitle(mergedArticle);
  const content = normalizeContent(mergedArticle?.content);
  const source = getSourceLabel(mergedArticle?.source);
  const date = formatDate(mergedArticle);
  const url = getBestUrl(mergedArticle);
  const videoUrl = getVideoUrl(mergedArticle);
  const hasValidVideo = isValidHttpUrl(videoUrl) && !videoFailed;

  useEffect(() => {
    onInteract?.(mergedArticle);
  }, [mergedArticle, onInteract]);

  return (
    <aside className="flex h-full min-h-0 flex-col bg-mainBg pt-4 font-poppins text-mainText">
      <div className="flex shrink-0 items-start gap-3 border-b border-surfaceBorder px-4 py-4">
        <div className="min-w-0 flex-1">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            {source && (
              <span
                className="rounded-full border bg-innerBg px-2.5 py-1 text-[10px] font-medium tracking-wide text-mutedText"
                style={{ borderColor: `rgba(${cyan},0.16)` }}
              >
                {source}
              </span>
            )}

            {date && (
              <span className="rounded-full border border-surfaceBorder bg-innerBg px-2.5 py-1 text-[10px] font-medium text-mutedText">
                {date}
              </span>
            )}
          </div>

          <h2 className="text-base font-semibold leading-6 text-mainText">
            {title}
          </h2>
        </div>

        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-surfaceBorder bg-innerBg text-mutedText transition hover:bg-btnHover hover:text-mainText"
            aria-label="Close details"
          >
            <X className="h-4 w-4" strokeWidth={2.2} />
          </button>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
        {status === "loading" && (
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-surfaceBorder bg-surface/60 px-3 py-2 text-xs text-mutedText">
            <LoadingSpinner />
            Loading details...
          </div>
        )}

        {status === "error" && (
          <div className="mb-4 flex gap-2 rounded-xl border border-errorText/25 bg-errorText/10 px-3 py-2 text-xs text-errorText">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <div>
              <div className="font-medium">Could not load full details.</div>
              <div className="mt-0.5 opacity-85">{error}</div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {hasValidVideo ? (
            <BrandedVideoPlayer
              article={mergedArticle}
              src={videoUrl}
              onError={() => setVideoFailed(true)}
            />
          ) : (
            <ImagePreview article={mergedArticle} />
          )}

          <CopyableUrl url={url} />

          <ExpandableDetailContent content={content} />
        </div>
      </div>
    </aside>
  );
};

export const useArticleSidebar = (articleCards = [], onInteract = () => {}) => {
  const { setIsRightOpen, setRightComponent, setRightSidebarMode, setRightId } =
    useRightSidebar();

  const [activeCardId, setActiveCardId] = useState(null);

  const handleCardClick = useCallback(
    (entryOrArticle) => {
      const entry = entryOrArticle?.article
        ? entryOrArticle
        : articleCards.find(
            (card) =>
              card?.article?.document_id === entryOrArticle?.document_id ||
              card?.article?.doc_id === entryOrArticle?.doc_id ||
              card?.article?.docID === entryOrArticle?.docID ||
              card?.article?.id === entryOrArticle?.id ||
              card?.id === entryOrArticle?.id,
          );

      const article = entry?.article ?? entryOrArticle;

      const cardId =
        entry?.id ||
        article?.document_id ||
        article?.doc_id ||
        article?.docID ||
        article?.id ||
        null;

      const docId = getDocId(article, cardId);

      setActiveCardId(cardId);
      setRightId?.(cardId);
      setRightSidebarMode?.(null);
      setIsRightOpen(true);

      setRightComponent(
        <ArticleCardDetailPanel
          article={article}
          cardId={docId}
          onInteract={(state) => onInteract(cardId, docId, state)}
          onClose={() => setIsRightOpen(false)}
        />,
      );
    },
    [
      articleCards,
      onInteract,
      setIsRightOpen,
      setRightComponent,
      setRightId,
      setRightSidebarMode,
    ],
  );

  return {
    activeCardId,
    setActiveCardId,
    handleCardClick,
  };
};

export default ArticleCardDetailPanel;
